import { DocumentsListsPolicyModule, InvoicesScheduleModule } from '@folio/modules';
import {
  BadgeCheck,
  BriefcaseIcon,
  Building2,
  CalendarDays,
  Car,
  Euro,
  FilePen,
  FileUp,
  Hash,
  Home,
  Mail,
  MapPin,
  PartyPopper,
  Percent,
  Phone,
  ScaleIcon,
  Shield,
  User,
  Users,
} from 'lucide-react';
import type { KorintModulesProductsConfig, FormSectionsWithShortcuts } from 'korint-sdk/react';
import { NewBusinessBrokerSecondaryResidenceProtectionJuridique } from '../custom-pages/NewBusinessBrokerSecondaryResidenceProtectionJuridique';
import { NewBusinessBrokerVehicleProtectionJuridique } from '../custom-pages/NewBusinessBrokerVehicleProtectionJuridique';
import { driverMtaRoutes } from './common';
import {
  derogationControllerCheckDerogationRequirement,
  derogationControllerCreateDerogation,
  getKorintErrorCode,
  policyControllerPushPolicyEvent,
} from 'korint-sdk';
import { ContactKey } from '@folio/utils/customerDisplayName';
import { IncludedPerilsProtectionJuridique } from '../custom-pages/IncludedPerilsProtectionJuridique';
import { centsToEuros, eurosToCents } from '@folio/utils';

const customerFunnelSections = [
  {
    title: 'Signataire',
    fields: [
      { name: 'contactCivility', icons: { MONSIEUR: User, MADAME: User, MADEMOISELLE: User } },
      {
        name: 'contactFirstName',
        required: true,
        type: 'text',
        colSpan: 6 as const,
        icon: User,
        DEPRECATED_placeholder: 'Michel',
        isCustomField: true,
      },
      {
        name: 'contactLastName',
        required: true,
        type: 'text',
        colSpan: 6 as const,
        icon: User,
        DEPRECATED_placeholder: 'Dupont',
        isCustomField: true,
      },
      {
        name: 'contactEmail',
        required: true,
        type: 'email',
        colSpan: 6 as const,
        icon: Mail,
        DEPRECATED_placeholder: 'michel.dupont@gmail.com',
        isCustomField: true,
      },
      {
        name: 'contactPhoneNumber',
        required: true,
        type: 'phoneNumber',
        colSpan: 6 as const,
        icon: Phone,
        countries: ['FR'],
        DEPRECATED_placeholder: '06 06 06 06 06',
        isCustomField: true,
      },
      {
        name: 'contactBirthDate',
        required: true,
        type: 'date',
        colSpan: 6 as const,
        icon: CalendarDays,
        DEPRECATED_placeholder: '01/01/1900',
        isCustomField: true,
      },
      {
        name: 'contactBirthPlace',
        required: true,
        type: 'text',
        colSpan: 6 as const,
        icon: MapPin,
        DEPRECATED_placeholder: 'Paris',
        isCustomField: true,
      },
      {
        name: 'contactAddress',
        required: true,
        type: 'address',
        icon: MapPin,
        DEPRECATED_placeholder: '123 rue de la Paix',
        isCustomField: true,
      },
      {
        name: 'contactPostCode',
        required: true,
        type: 'postcode',
        countries: ['FR'],
        colSpan: 4 as const,
        icon: Hash,
        DEPRECATED_placeholder: '75001',
        isCustomField: true,
      },
      {
        name: 'contactCity',
        required: true,
        type: 'city',
        colSpan: 8 as const,
        icon: Building2,
        DEPRECATED_placeholder: 'Paris',
        isCustomField: true,
      },
      {
        name: 'contactMaritalStatus',
        required: true,
        type: 'select',
        options: [
          { value: 'single', label: 'Célibataire' },
          { value: 'married', label: 'Marié' },
          { value: 'pacs', label: 'Pacsé' },
          { value: 'divorced', label: 'Divorcé' },
          { value: 'widowed', label: 'Veuf' },
        ],
        icons: {
          single: User,
          married: Users,
          pacs: Users,
          divorced: User,
          widowed: User,
        },
        perRow: 3,
        isCustomField: true,
      },
      {
        name: 'contactProfession',
        required: true,
        type: 'text',
        icon: BriefcaseIcon,
        DEPRECATED_placeholder: 'Ingénieur(e)',
        isCustomField: true,
      },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const quotePolicySections = [
  {
    title: 'Frais et commissions',
    fields: [
      {
        name: 'managementFeeTaxesIncluded',
        isCustomField: true,
        type: 'number',
        required: false,
        min: 0,
        colSpan: 6 as const,
        icon: Euro,
        DEPRECATED_placeholder: '5',
        apiDataAccessor: apiData =>
          centsToEuros(apiData.customFields.managementFeeTaxesIncluded) ?? 0,
        formDataAccessor: formData =>
          formData.managementFeeTaxesIncluded
            ? eurosToCents(formData.managementFeeTaxesIncluded as number)
            : undefined,
      },
      {
        name: 'retailFee',
        type: 'number',
        required: false,
        min: 0,
        colSpan: 6 as const,
        icon: Percent,
        DEPRECATED_placeholder: '10',
        apiDataAccessor: apiData =>
          apiData.brokerageFees?.find(
            ({ brokerageFirmId }: { brokerageFirmId: string; fee: number }) =>
              brokerageFirmId === apiData.brokerageFirm,
          )?.fee ?? 0,
        formDataAccessor: formData => ({
          fees: [
            {
              brokerageFirmId: formData.brokerageFirm,
              fee: Number(formData.retailFee ?? 0),
            },
          ],
        }),
      },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const quoteAssetSections = [
  {
    title: 'Date de début de contrat',
    fields: [
      {
        name: 'startedAt',
        required: true,
        type: 'datetime',
        min: 'now',
        icon: CalendarDays,
      },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const policyHolderInvitationRole = ['DRIVER'];

export const tutassurProtectionJuridiqueConfig = {
  display: {
    category: 'protection',
    icon: ScaleIcon,
    badges: ['online'],
  },
  productCustomPath: '/protection-juridique',
  quoteTierSelectModuleConfig: {
    customIncludedPerils: IncludedPerilsProtectionJuridique,
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: true,
  },
  priceBreakdownConfig: {
    groups: [
      { lines: [{ key: 'INSURANCE_PREMIUM', variant: 'bold' }] },
      { variant: 'callout', lines: [{ key: 'MANAGEMENT_FEE' }] },
      { lines: [{ key: 'RETAIL_COMMISSION', variant: 'bold' }] },
    ],
  },
  customerInformations: {
    fieldsConfig: [
      {
        key: 'contactCivility',
        type: 'text',
      },
      {
        key: 'contactFirstName',
        type: 'text',
        contactKey: ContactKey.FIRST_NAME,
      },
      {
        key: 'contactLastName',
        type: 'text',
        contactKey: ContactKey.LAST_NAME,
      },
      {
        key: 'contactEmail',
        type: 'email',
      },
      {
        key: 'contactPhoneNumber',
        type: 'tel',
      },
      {
        key: 'contactBirthDate',
        type: 'date',
      },
      {
        key: 'contactBirthPlace',
        type: 'text',
      },
      {
        key: 'contactProfession',
        type: 'text',
      },
      {
        key: 'contactMaritalStatus',
        type: 'text',
      },
      {
        key: 'contactAddress',
        type: 'text',
      },
      {
        key: 'contactPostCode',
        type: 'postcode',
        countries: ['FR'],
      },
      {
        key: 'contactCity',
        type: 'text',
      },
      {
        key: 'contactSecondaryAddress1',
        type: 'text',
      },
      {
        key: 'contactSecondaryPostCode1',
        type: 'text',
      },
      {
        key: 'contactSecondaryCity1',
        type: 'text',
      },
      {
        key: 'contactSecondaryAddress2',
        type: 'text',
      },
      {
        key: 'contactSecondaryPostCode2',
        type: 'text',
      },
      {
        key: 'contactSecondaryCity2',
        type: 'text',
      },
      {
        key: 'registrationNumber1',
        type: 'text',
      },
      {
        key: 'brand1',
        type: 'text',
      },
      {
        key: 'model1',
        type: 'text',
      },
      {
        key: 'registrationNumber2',
        type: 'text',
      },
      {
        key: 'brand2',
        type: 'text',
      },
      {
        key: 'model2',
        type: 'text',
      },
      {
        key: 'registrationNumber3',
        type: 'text',
      },
      {
        key: 'brand3',
        type: 'text',
      },
      {
        key: 'model3',
        type: 'text',
      },
      {
        key: 'registrationNumber4',
        type: 'text',
      },
      {
        key: 'brand4',
        type: 'text',
      },
      {
        key: 'model4',
        type: 'text',
      },
      {
        key: 'registrationNumber5',
        type: 'text',
      },
      {
        key: 'brand5',
        type: 'text',
      },
      {
        key: 'model5',
        type: 'text',
      },
    ],
  },
  enableAssetComparison: false,
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/protection-juridique/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery/protection-juridique/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: true,
  },
  policyholderInvitationModuleConfig: {
    relationTypes: policyHolderInvitationRole,
  },
  discoveryFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'documents-upload',
        type: 'documentUploadFunnel' as const,
        context: 'policy' as const,
        icon: FileUp,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: User,
        sections: customerFunnelSections,
      },
      {
        path: 'asset',
        type: 'emptyAssetFunnel',
        hidden: true,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'vehicle',
        type: 'custom',
        icon: Car,
        component: <NewBusinessBrokerVehicleProtectionJuridique />,
      },
      {
        path: 'secondary-residence',
        type: 'custom',
        icon: Home,
        component: <NewBusinessBrokerSecondaryResidenceProtectionJuridique />,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        policySections: quotePolicySections,
        assetSections: quoteAssetSections,
      },
      {
        path: 'documents',
        type: 'module',
        icon: FileUp,
        module: (
          <DocumentsListsPolicyModule
            displayContext="policyholder"
            categories={[
              'QUOTE',
              'TERMS_AND_CONDITIONS',
              'IPID_PROTECTION_JURIDIQUE',
              'INFORMATION_SHEET',
            ]}
            pollUntilAllDocumentsAreLoaded={true}
          />
        ),
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'new-business-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
      },
    ],
  },
  mtaFunnelSteps: {
    steps: [
      {
        path: 'new',
        type: 'newMTACreation',
        icon: FilePen,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: User,
        sections: customerFunnelSections,
      },
      {
        path: 'vehicle',
        type: 'custom',
        icon: Car,
        component: <NewBusinessBrokerVehicleProtectionJuridique />,
      },
      {
        path: 'secondary-residence',
        type: 'custom',
        icon: Home,
        component: <NewBusinessBrokerSecondaryResidenceProtectionJuridique />,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        policySections: quotePolicySections,
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'mta-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
        callback: {
          callToActionCallback: async ({ branchId }) => {
            const { needsDerogation } = await derogationControllerCheckDerogationRequirement(
              branchId,
              {},
              { action: 'CONFIRM' },
            );

            if (!needsDerogation) {
              return;
            }

            try {
              await derogationControllerCreateDerogation(branchId, { action: 'CONFIRM' });
            } catch (error) {
              if (getKorintErrorCode(error) !== 'DEROGATION_ALREADY_EXISTS') {
                throw error;
              }
            }
          },
        },
      },
    ],
  },
  policyholderMtaFunnelSteps: driverMtaRoutes,
  finalizeNewBusinessModuleConfig: {
    buttons: [
      {
        id: 'send-via-email-after-derogation-approval',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          policyStatuses: ['POLICY_QUOTED'],
          branchTypes: ['MTA'],
          derogationStatuses: ['APPROVED'],
        },
      },
      {
        id: 'send-via-email',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          firmTypes: ['RETAIL', 'WHOLESALE'],
          policyStatuses: ['POLICY_QUOTED'],
          branchTypes: ['NEW_BUSINESS'],
          needsDerogation: false,
        },
      },
    ],
    rules: [
      {
        id: 'documents-import',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ policy }) => policy.customFields.allClientDocumentsOk === true,
      },
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: ({ paymentMethods }) =>
          paymentMethods.some(({ paymentMethodType }) => paymentMethodType === 'sepa_debit'),
      },
    ],
  },
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: true,
    isQuoteTierModuleVisible: true,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
  },
} satisfies KorintModulesProductsConfig;
